// frame: tC8FY @ ui/dogdisk-user.pen
// spec: docs/ui/user-page-flows.md §2.4 tC8FY 页面-文件夹详情
//
// /folders/:id 直链入口。当前 files.tsx 已有多级文件夹浏览(crumbs 内部
// state),但缺 URL 持久化;本页负责:
//   1. uuid 非法 → 渲染 NotFoundView
//   2. 拉取文件夹 meta(name / parent_id)用于面包屑首屏
//   3. 复用 FilesPage 同款 v_file_info_mine 查询(parent_id=eq.:id)
//   4. 上拉到根目录用 navigate('/files')

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  cn,
  formatBytes,
  formatRelativeTime,
  getSupabase,
  useCurrentUser,
} from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen } from '../components/mobile-screen'
import { TabBar } from '../components/tab-bar'
import { FileActions } from '../components/file-actions'
import { CreateMenu } from '../components/create-menu'
import { FileThumb } from '../components/file-thumb'
import { FileSectionToolbar } from '../components/file-section-toolbar'
import { MultiSelectHeader } from '../components/multi-select-header'
import { BatchActionBar } from '../components/batch-action-bar'
import { SortFilterSheet } from '../components/modals/sort-filter-sheet'
import { fileOpenHref } from '../_lib/file-route'
import { NotFoundView } from './errors/not-found'
import type { FileRow } from '../_lib/offline-cache'
import { MINE_FILE_COLS, fileRowFromMine, type MineFileRow } from '../_lib/file-query'
import { CoverPicker } from '../components/modals/cover-picker'
import { useSetCover } from '../_lib/set-cover'
import { DEFAULT_SORT, sortFileRows, type SortState } from '../_lib/file-sort'
import { useMultiSelect } from '../_lib/use-multi-select'
import { useFolderAncestors } from '../_lib/use-folder-ancestors'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

type FolderMeta = {
  id: string
  name: string
  parent_id: string | null
  cover_file_info_id: string | null
}

function useFolderMeta(folderId: string) {
  return useQuery({
    queryKey: ['folder', 'meta', folderId] as const,
    enabled: UUID_RE.test(folderId),
    queryFn: async (): Promise<FolderMeta | null> => {
      const sb = getSupabase()
      // owner 收口走 v_file_info_mine(内建 user_id=auth.uid() + status='active')。
      const { data, error } = await sb
        .from('v_file_info_mine')
        .select('id, name, parent_id, type, cover_file_info_id')
        .eq('id', folderId)
        .eq('type', 'folder')
        .maybeSingle()
      if (error || !data) return null
      return {
        id: data.id,
        name: data.name,
        parent_id: data.parent_id,
        cover_file_info_id: data.cover_file_info_id ?? null,
      }
    },
  })
}

function useFolderChildren(folderId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['file', 'list', folderId] as const,
    enabled,
    queryFn: async (): Promise<FileRow[]> => {
      const sb = getSupabase()
      // owner 收口走 v_file_info_mine(内建 user_id=auth.uid());size/cover 已在 view 内平坦化。
      const { data, error } = await sb
        .from('v_file_info_mine')
        .select(MINE_FILE_COLS)
        .eq('parent_id', folderId)
        .order('type', { ascending: false })
        .order('created_at', { ascending: false })
      if (error) throw error
      return ((data ?? []) as unknown as MineFileRow[]).map(fileRowFromMine)
    },
  })
}

export function FolderDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { status: authStatus } = useCurrentUser()
  const isAuth = authStatus === 'authenticated'

  const { data: meta, isLoading: metaLoading } = useFolderMeta(id)
  const { data: ancestors = [] } = useFolderAncestors(id)
  const { data: children = [], isLoading: childrenLoading, refetch } = useFolderChildren(
    id,
    isAuth && Boolean(meta),
  )
  const [actionFor, setActionFor] = useState<FileRow | null>(null)
  const [coverOpen, setCoverOpen] = useState(false)
  const setCover = useSetCover('file_info', id, ['file', 'list', id])

  // 多选模式 + 排序(必须在所有 early return 之前,遵循 React Rules of Hooks)
  const ms = useMultiSelect()
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const [sortOpen, setSortOpen] = useState(false)
  const sortedChildren = useMemo(() => sortFileRows(children, sort), [children, sort])
  useEffect(() => {
    ms.registerAll(sortedChildren.map((r) => r.id))
    // ms.registerAll 由 useCallback(fn,[]) 封装,引用永远稳定
  }, [sortedChildren, ms.registerAll])
  const selectedRows = sortedChildren.filter((r) => ms.ids.has(r.id))

  // meta 加载失败 / null → 当 404
  useEffect(() => {
    // 占位 — 保持 hook 顺序稳定;真实逻辑在渲染路径处理。
  }, [meta])

  // UUID 守卫放在所有 hook 之后，避免违反 React Rules of Hooks（条件性 hook）。
  // useFolderMeta/useFolderChildren 内部的 enabled: UUID_RE.test(folderId) 已防止
  // 非法 id 发起请求；此处只处理渲染分支。
  if (!UUID_RE.test(id)) {
    return <NotFoundView reason="invalid_folder_id" />
  }

  if (metaLoading) {
    return (
      <AppLayout>
        <MobileScreen>
          <div className="flex flex-1 items-center justify-center text-[13px] text-[#9CA3AF]">
            加载中…
          </div>
        </MobileScreen>
      </AppLayout>
    )
  }

  if (!meta) {
    return <NotFoundView reason="folder_not_found" />
  }

  return (
    <AppLayout>
      <MobileScreen>
        <div data-testid="tC8FY" className="flex flex-1 flex-col">
          {/* .pen klMEc header — gap 12, padding [10,24,12,24], 顶部响应 safe-area */}
          <header className="flex flex-col gap-3 px-6 pb-3 pt-[max(10px,env(safe-area-inset-top))]">
            {/* .pen UPmeQ headerRow — 多选模式下替换为 MultiSelectHeader(YM9uv);面包屑保留 */}
            {ms.active ? (
              <MultiSelectHeader
                count={ms.count}
                allSelected={ms.allSelected}
                onCancel={ms.exit}
                onToggleAll={ms.toggleAll}
              />
            ) : (
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  data-testid="nt3gn"
                  aria-label="返回上级"
                  onClick={() => {
                    if (meta.parent_id) navigate(`/folders/${meta.parent_id}`)
                    else navigate('/files')
                  }}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F6F7F8] active:bg-[#EDEEF0]"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2.4"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
                {/* .pen KSqvj folderTitle — Bricolage Grotesque 22/600 */}
                <h1
                  className="flex-1 truncate text-center text-[22px] font-semibold text-[#1A1A1A]"
                  style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}
                >
                  {meta.name}
                </h1>
                {/* .pen QfOPz setCoverBtn — 44×44 圆 灰底 image-icon */}
                <button
                  type="button"
                  data-testid="QfOPz"
                  aria-label="设置文件夹封面"
                  onClick={() => setCoverOpen(true)}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F6F7F8] active:bg-[#EDEEF0]"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1971ca" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.6-3.6a2 2 0 0 0-2.8 0L7 19" />
                  </svg>
                </button>
                {/* .pen IXNml moreBtn — 44×44 圆 灰底 ellipsis-vertical;点击弹 BQOCz ActionSheet */}
                <button
                  type="button"
                  data-testid="IXNml"
                  aria-label="更多操作"
                  onClick={() => setActionFor({
                    id: meta.id,
                    name: meta.name,
                    type: 'folder',
                    status: 'active',
                    parent_id: meta.parent_id,
                    created_at: new Date().toISOString(),
                  })}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F6F7F8] active:bg-[#EDEEF0]"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2.2" strokeLinecap="round">
                    <circle cx="12" cy="5" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="12" cy="19" r="1.4" />
                  </svg>
                </button>
              </div>
            )}

            {/* .pen ULEMM breadcrumb — gap 8, chevron-right sep, #19caad/(#6B7280 current)
                YM9uv:多选模式下面包屑保留(不隐藏) */}
            <nav data-testid="folder-breadcrumb" className="flex flex-wrap items-center gap-2 text-[14px] font-medium">
              <button
                type="button"
                data-testid="pExsD"
                onClick={() => navigate('/files')}
                className="text-[#19caad]"
              >
                我的网盘
              </button>
              {/* 完整祖先链(根→当前);未就绪时退化为仅当前(meta.name)。全显示 + flex-wrap 换行。 */}
              {(ancestors.length > 0 ? ancestors : [{ id, name: meta.name }]).map((node, i, arr) => {
                const isCurrent = i === arr.length - 1
                return (
                  <span key={node.id} className="flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                    {isCurrent ? (
                      <span className="truncate text-[#6B7280]">{node.name}</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => navigate(`/folders/${node.id}`)}
                        className="truncate text-[#19caad]"
                      >
                        {node.name}
                      </button>
                    )}
                  </span>
                )
              })}
            </nav>
          </header>

          {/* .pen BYeJZ sectionHeader — FileSectionToolbar(文件夹内容 + 多选 + 排序) */}
          <FileSectionToolbar
            frameId="BYeJZ"
            title="文件夹内容"
            count={sortedChildren.length}
            onMultiSelect={ms.enter}
            onSort={() => setSortOpen(true)}
          />

          {/* .pen 5Acz0 fileList — gap 12 / item: h 72 rounded 16 灰底 padding 16 gap 14 */}
          <ul className="flex flex-col gap-3 px-6 pb-safe-tab">
            {childrenLoading ? (
              [1, 2, 3].map((i) => (
                <li key={i} className="flex items-center gap-[14px] rounded-2xl bg-[#F6F7F8] p-4">
                  <span className="skeleton h-11 w-11 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <span className="skeleton block h-3 w-3/5 rounded" />
                    <span className="skeleton block h-2 w-2/5 rounded" />
                  </div>
                </li>
              ))
            ) : sortedChildren.length === 0 ? (
              <li className="flex flex-col items-center gap-2 py-12 text-[13px] text-[#9CA3AF]">
                <p>此文件夹为空</p>
                <p className="text-[11px]">点击右下角 + 上传文件或新建子文件夹</p>
              </li>
            ) : (
              sortedChildren.map((f) => {
                const selected = ms.ids.has(f.id)
                return (
                  <li
                    key={f.id}
                    data-testid={`files-row-${f.id}`}
                    className={cn(
                      'flex items-center gap-[14px] rounded-2xl p-4',
                      ms.active && selected
                        ? 'bg-[#E9F8F5] ring-[1.5px] ring-inset ring-[#19caad]'
                        : 'bg-[#F6F7F8]',
                    )}
                  >
                    {ms.active && (
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={selected}
                        data-testid="files-row-checkbox"
                        onClick={() => ms.toggle(f.id)}
                        className={cn(
                          'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[11px]',
                          selected ? 'bg-[#19caad]' : 'border-[1.5px] border-[#D1D5DB] bg-white',
                        )}
                      >
                        {selected && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid="files-row-open-btn"
                      onClick={() => {
                        if (ms.active) {
                          ms.toggle(f.id)
                          return
                        }
                        navigate(fileOpenHref(f))
                      }}
                      className="flex min-w-0 flex-1 items-center gap-[14px] text-left"
                    >
                      <FileThumb row={f} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[16px] font-semibold text-[#1A1A1A]">{f.name}</p>
                        <p className="mt-1 text-[12px] text-[#9CA3AF]">
                          {f.type === 'folder'
                            ? `文件夹 · ${formatRelativeTime(f.created_at)}`
                            : `${f.size ? formatBytes(f.size) : ''} · ${formatRelativeTime(f.created_at)}`}
                        </p>
                      </div>
                    </button>
                    {!ms.active && (
                      <button
                        type="button"
                        aria-label="更多"
                        onClick={() => setActionFor(f)}
                        className="flex h-8 w-6 items-center justify-center text-[#9CA3AF]"
                      >
                        <svg width="4" height="16" viewBox="0 0 4 16" fill="currentColor">
                          <circle cx="2" cy="2" r="1.5" />
                          <circle cx="2" cy="8" r="1.5" />
                          <circle cx="2" cy="14" r="1.5" />
                        </svg>
                      </button>
                    )}
                  </li>
                )
              })
            )}
          </ul>

          {isAuth && !ms.active && (
            <CreateMenu parentId={id} onChanged={() => void refetch()} />
          )}

          {ms.active ? <BatchActionBar files={selectedRows} onDone={ms.exit} /> : <TabBar />}

          {actionFor && (
            <FileActions
              file={actionFor}
              parentId={id}
              onClose={() => setActionFor(null)}
            />
          )}

          <CoverPicker
            open={coverOpen}
            onClose={() => setCoverOpen(false)}
            currentCoverId={meta.cover_file_info_id ?? null}
            defaultFolderId={id}
            onSelect={(pick) => setCover.mutate(pick.id)}
            onClear={() => setCover.mutate(null)}
          />

          <SortFilterSheet open={sortOpen} onClose={() => setSortOpen(false)} value={sort} onChange={setSort} />
        </div>
      </MobileScreen>
    </AppLayout>
  )
}
